import PageLayout from '@/components/PageLayout';
import PageHeader from '@/components/PageHeader';
import ApplicationShareButton from 'containers/ApplicationContainer/components/ApplicationShareButton';
import ContentSection, { ContentSectionFlushContent } from '@/components/ContentSection';
import { entryPoint } from 'react_rails/entryPoint';
import Sponsor from 'components/Sponsor';
import styles from './styles/Index.module.scss';
import { TeamPlayersIndexQuery, TeamPlayersIndexQueryVariables, rubyQueries } from 'queries';
import MissingInfoModule from '@/components/modules/MissingInfoModule';
import HeadlinesModule from '@/components/modules/HeadlinesModule';
import ShareModalDefaultExports from 'components/ShareModalDefaultExports';
import Page from '@/components/Page';
import TeamSubNav from '@/components/subNavs/TeamSubNav';
import { CURRENT_SCHOOL_YEAR } from 'constants/schoolYears';
import Table, { TableCell, TableHeaderCell, TableLinkRow, TableRow } from '@/components/Table';
import TextLink, { TextColorVariant, TextLinkSize } from '@/components/TextLink';
import Filter from '@/components/Filter';
import ProfileImage from 'components/ProfileImage';
import filterArticles from 'helpers/filterArticles';
import { useApplicationContainer } from 'containers/ApplicationContainer';
import { useState } from 'react';
import InfinitePagination from '@/components/InfinitePagination';
import MinuteMediaAd from '@/components/ads/MinuteMediaAd';
import useScreenSize from 'hooks/useScreenSize';
import StnVideoAd from '@/components/ads/StnVideoAd';
import FilterSection from '@/components/FilterSection';
import OutbrainAd from '@/components/ads/OutbrainAd';

export default entryPoint(function Roster({
  query,
  variables
}: {
  query: TeamPlayersIndexQuery;
  variables: TeamPlayersIndexQueryVariables;
}) {
  const { team } = query;
  const { schoolYear } = variables;

  const [, { updateCurrentQueryStringParams }] = useApplicationContainer();
  const { teamPlayers, webStatsPath: statsPath, teamCoaches: coaches } = team;
  const [teamPlayerObjects, setTeamPlayerObjects] = useState(teamPlayers.nodes);
  const { isSmallScreen } = useScreenSize();

  // TODO fix this on api sie
  const schoolYears = team.schoolYears;
  if (!schoolYears.includes(CURRENT_SCHOOL_YEAR)) {
    schoolYears.push(CURRENT_SCHOOL_YEAR);
  }

  function handleExport() {
    const tables = [
      {
        title: `${team.name}`,
        subTitle: `${schoolYear || CURRENT_SCHOOL_YEAR} ${team.genderSport?.name} ${team.level.name} Roster`,
        header: ['No', 'Name', 'Position', 'Height', 'Weight', 'Year'],
        body: teamPlayerObjects.map(teamPlayer => [
          teamPlayer.jerseyNumber,
          teamPlayer.player.name,
          [teamPlayer.position?.abbrev, teamPlayer.secondaryPosition?.abbrev].filter(Boolean).join('/'),
          teamPlayer.player.heightText,
          teamPlayer.player.weightText,
          teamPlayer.player.classYear
        ])
      }
    ];

    if (coaches && coaches.length > 0) {
      tables.push({
        title: 'Coaches',
        subTitle: `${schoolYear || CURRENT_SCHOOL_YEAR} ${team.genderSport?.name} ${team.level.name} Coaches`,
        header: ['Name', 'Position', 'Experience'],
        body: coaches.map(({ coach, coachRoleText, year_hired_text }) => [
          `${coach.firstName} ${coach.lastName}`,
          coachRoleText,
          year_hired_text
        ])
      });
    }

    return {
      title: `${team.name}`,
      subTitle: `${schoolYear || CURRENT_SCHOOL_YEAR} ${team.genderSport?.name} ${team.level.name} Roster`,
      tables: tables
    };
  }

  const genderSportName = team.genderSport?.name;
  const articles = filterArticles(team.articles.nodes);

  return (
    <Page subNav={<TeamSubNav team={team} />}>
      <PageLayout
        sidebar={
          <>
            <MinuteMediaAd placement="SIDEBAR" />
            {articles.length > 0 && <HeadlinesModule articles={articles} href={team.webNewsPath} title="Related News" />}
            <MinuteMediaAd placement="SIDEBAR_2" />
          </>
        }
      >
        {!isSmallScreen && (
          <div style={{ width: '100%' }}>
            <StnVideoAd placement="INLINE" />
          </div>
        )}
        <ContentSection>
          <PageHeader
            right={
              <>
                <ApplicationShareButton>{teamPlayerObjects && <ShareModalDefaultExports data={handleExport} />}</ApplicationShareButton>
              </>
            }
          >
            {team.name} {genderSportName} Roster
          </PageHeader>
          <FilterSection>
            <Filter
              pathExtractor={season => updateCurrentQueryStringParams({ season })}
              value={schoolYear || CURRENT_SCHOOL_YEAR}
              options={schoolYears}
            />
          </FilterSection>
          <ContentSectionFlushContent>
            {!teamPlayerObjects ? (
              <MissingInfoModule />
            ) : (
              <InfinitePagination
                onLoadMore={({
                  team: {
                    teamPlayers: { nodes, pageInfo }
                  }
                }) => {
                  setTeamPlayerObjects(teamPlayerObjects => [...teamPlayerObjects, ...nodes]);
                  return pageInfo;
                }}
                pageInfo={query.team.teamPlayers.pageInfo}
                query={rubyQueries.teamPlayersIndexQuery}
                ttl={30}
                variables={{ ...variables, paginating: true }}
              >
                <Table
                  className={styles.PlayersTable}
                  header={
                    <>
                      <TableHeaderCell>#</TableHeaderCell>
                      <TableHeaderCell>Players</TableHeaderCell>
                      <TableHeaderCell align="right">Pos 1</TableHeaderCell>
                      <TableHeaderCell align="right">Pos 2</TableHeaderCell>
                      <TableHeaderCell align="right">Year</TableHeaderCell>
                      <TableHeaderCell align="right">Height</TableHeaderCell>
                      <TableHeaderCell align="right">Weight</TableHeaderCell>
                    </>
                  }
                >
                  {teamPlayerObjects.map(teamPlayer => (
                    <TableLinkRow key={teamPlayer.player.id} href={teamPlayer.webPath}>
                      <TableCell className={styles.JerseyNumber}>{teamPlayer.jerseyNumber}</TableCell>
                      <TableCell>
                        <TextLink href={teamPlayer.webPath} size={TextLinkSize.SMALL} color={TextColorVariant.BLACK}>
                          <ProfileImage
                            className={styles.ProfileImage}
                            src={teamPlayer.player.image}
                            alt={teamPlayer.player.name}
                            genderSport={team.genderSport?.slug}
                          />
                          {teamPlayer.player.name}
                        </TextLink>
                      </TableCell>
                      <TableCell align="right">{teamPlayer.position?.abbrev}</TableCell>
                      <TableCell align="right">{teamPlayer.secondaryPosition?.abbrev}</TableCell>
                      <TableCell align="right">{teamPlayer.player.formattedClassYearAbbrev || '-'}</TableCell>
                      <TableCell align="right">{teamPlayer.player.heightText}</TableCell>
                      <TableCell align="right">{teamPlayer.player.weightText}</TableCell>
                    </TableLinkRow>
                  ))}
                </Table>
              </InfinitePagination>
            )}
          </ContentSectionFlushContent>
        </ContentSection>
        <div style={{ width: '100%', paddingTop: '20px' }}>
          <MinuteMediaAd placement="IN_FEED" />
        </div>
        <ContentSection>
          <PageHeader>Coaches</PageHeader>
          <ContentSectionFlushContent>
            {coaches?.length === 0 ? (
              <MissingInfoModule />
            ) : (
              <Table
                className={styles.CoachesTable}
                header={
                  <>
                    <TableHeaderCell>Coaches</TableHeaderCell>
                    <TableHeaderCell align="right">Position</TableHeaderCell>
                    <TableHeaderCell align="right">Exp</TableHeaderCell>
                  </>
                }
              >
                {coaches?.map(coachObject => (
                  <TableRow key={coachObject.id}>
                    <TableCell>
                      <div className={styles.CoachCell}>
                        <ProfileImage
                          className={styles.ProfileImage}
                          src={coachObject.coach.image}
                          alt={coachObject.coach.lastName}
                          genderSport={team.genderSport?.slug}
                        />
                        {coachObject.coach.firstName} {coachObject.coach.lastName}
                      </div>
                    </TableCell>

                    <TableCell align="right">{coachObject.coachRoleText}</TableCell>
                    <TableCell align="right">{coachObject.year_hired_text}</TableCell>
                  </TableRow>
                ))}
              </Table>
            )}
          </ContentSectionFlushContent>
        </ContentSection>
        <div style={{ width: '100%', paddingTop: '20px' }}>
          <MinuteMediaAd placement="IN_FEED_2" />
        </div>
        <OutbrainAd />
      </PageLayout>
    </Page>
  );
});
